import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ChevronDown, Plus, Eye, Edit, Trash2 } from "lucide-react";
import { notifications } from "@mantine/notifications";
import { TextInput, Modal, Select, NumberInput, Button } from "@mantine/core";
import { useForm } from "@mantine/form";
import { Endpoints } from "../../../lib/endpoints";
import { PerformRequest } from "../../../lib/usePerformRequest";
import { TableLoader } from "../../../components/Loader/LoadingOverlay";
import { ButtonLoader } from "../../../components/Loader";

interface Transaction {
  id: string;
  property_type?: string;
  units?: string;
  client_name?: string;
  user?: string;
  amount?: number;
  status?:
    | "PAID"
    | "PENDING"
    | "PROCESSING"
    | "pending"
    | "paid"
    | "processing";
  date?: string;
  time?: string;
  reference?: string;
  created_at?: string;
  updated_at?: string;
  [key: string]: any;
}

interface TransactionsResponse {
  results?: Transaction[];
  count?: number;
  next?: string | null;
  previous?: string | null;
}

export default function Transactions() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("Newest");
  const [isLoading, setIsLoading] = useState(true);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] =
    useState<Transaction | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);

  const updateForm = useForm({
    initialValues: {
      user: "",
      amount: "",
      status: "pending",
    },
    validate: {
      user: (value: string) => {
        if (!value?.trim()) return "User email is required";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return "Please enter a valid email address";
        }
        return null;
      },
      amount: (value: string | number | null | undefined) => {
        if (value === null || value === undefined || value === "") {
          return "Amount is required";
        }
        const numValue = parseFloat(String(value).replace(/[^\d.]/g, ""));
        if (isNaN(numValue) || numValue <= 0) {
          return "Please enter a valid amount";
        }
        return null;
      },
      status: (value: string) => (!value?.trim() ? "Status is required" : null),
    },
  });

  useEffect(() => {
    fetchTransactions();
  }, [currentPage, searchQuery]);

  async function fetchTransactions() {
    setIsLoading(true);
    try {
      const endpoint = Endpoints.GetTransactions({
        page: currentPage,
        search: searchQuery || undefined,
      });
      const response = await PerformRequest<TransactionsResponse>({
        method: endpoint.method,
        route: endpoint.route,
      });

      if (response.status === 200 && response.data) {
        const data = Array.isArray(response.data)
          ? { results: response.data, count: response.data.length }
          : response.data;
        setTransactions(data?.results || []);
        const totalCount = data?.count ?? 0;
        setTotalPages(Math.ceil(totalCount / 10) || 1);
      } else {
        const errorMessage =
          response?.message || "Failed to load transactions. Please try again.";
        notifications.show({
          title: "Error",
          message: errorMessage,
          color: "red",
        });
      }
    } catch (error: any) {
      const errorMessage =
        error?.message || "Failed to load transactions. Please try again.";
      notifications.show({
        title: "Error",
        message: errorMessage,
        color: "red",
      });
    } finally {
      setIsLoading(false);
    }
  }

  async function handleUpdateTransaction(values: typeof updateForm.values) {
    if (!selectedTransaction?.id) return;

    setIsSubmitting(true);
    try {
      const endpoint = Endpoints.UpdateTransaction(selectedTransaction.id);
      const response = await PerformRequest<Transaction>({
        method: endpoint.method,
        route: endpoint.route,
        data: {
          user: values.user.trim(),
          amount: parseFloat(String(values.amount).replace(/[^\d.]/g, "")),
          status: values.status,
        },
      });

      if (response.status === 200 || response.status === 201) {
        notifications.show({
          title: "Success",
          message: "Transaction updated successfully!",
          color: "green",
        });
        setIsUpdateModalOpen(false);
        setSelectedTransaction(null);
        updateForm.reset();
        fetchTransactions();
      } else {
        const errorMessage =
          response?.message ||
          "Failed to update transaction. Please try again.";
        notifications.show({
          title: "Error",
          message: errorMessage,
          color: "red",
        });
      }
    } catch (error: any) {
      const errorMessage =
        error?.message || "Failed to update transaction. Please try again.";
      notifications.show({
        title: "Error",
        message: errorMessage,
        color: "red",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleDeleteTransaction(transactionId: string) {
    if (
      !window.confirm(
        "Are you sure you want to delete this transaction? This action cannot be undone."
      )
    ) {
      return;
    }

    setIsDeleting(transactionId);
    try {
      // Note: There's no DeleteTransaction endpoint in the Postman collection
      // If needed, we can add it to endpoints/index.ts
      notifications.show({
        title: "Warning",
        message:
          "Delete transaction functionality is not available in the API.",
        color: "yellow",
      });
    } catch (error: any) {
      const errorMessage =
        error?.message || "Failed to delete transaction. Please try again.";
      notifications.show({
        title: "Error",
        message: errorMessage,
        color: "red",
      });
    } finally {
      setIsDeleting(null);
    }
  }

  function handleEditTransaction(transaction: Transaction) {
    setSelectedTransaction(transaction);
    updateForm.setValues({
      user: transaction?.user || transaction?.client_name || "",
      amount: transaction?.amount?.toString() || "",
      status: (transaction?.status?.toLowerCase() || "pending") as string,
    });
    setIsUpdateModalOpen(true);
  }

  function handleViewTransaction(transaction: Transaction) {
    // Navigate to detail page or open detail modal
    if (transaction?.id) {
      navigate(`/d/transactions/${transaction.id}`);
    }
  }

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const rows = transactions.length > 0 ? transactions : [];

  const statusOptions = [
    { value: "pending", label: "Pending" },
    { value: "processing", label: "Processing" },
    { value: "paid", label: "Paid" },
  ];

  // Remove create modal - using dedicated page instead

  return (
    <div className="dashboard-page">
      <div className="dashboard-page__header">
        <div className="dashboard-page__header-left">
          <h1 className="dashboard-page__title">All Transactions</h1>
        </div>
        <button
          onClick={() => navigate("/d/new-transaction")}
          className="dashboard-button flex items-center justify-center gap-2"
          type="button"
          disabled={isLoading}
        >
          <Plus size={14} />
          <span>Create Transaction</span>
        </button>
      </div>

      <div className="dashboard-filters">
        <TextInput
          placeholder="Search transaction..."
          value={searchQuery}
          onChange={(e) => handleSearch(e.currentTarget.value)}
          leftSection={<Search size={18} />}
          style={{ flex: 1 }}
        />

        <div className="dashboard-sort">
          <span className="dashboard-sort__label">{sortBy}</span>
          <button
            className="dashboard-sort__button"
            type="button"
            onClick={() => setSortBy(sortBy === "Newest" ? "Oldest" : "Newest")}
          >
            <ChevronDown size={16} />
          </button>
        </div>
      </div>

      <section className="dashboard-panel shadow-[var(--card-shadow)] rounded-2xl">
        <div className="dashboard-table-wrap">
          <table className="dashboard-table">
            <thead>
              <tr>
                <th>User/Client</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Date</th>
                <th>Transaction ID</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <TableLoader colSpan={6} />
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-8">
                    No transactions found
                  </td>
                </tr>
              ) : (
                rows.map((tx, index) => {
                  const statusValue = tx?.status?.toLowerCase() || "pending";
                  const statusTone =
                    statusValue === "paid"
                      ? "success"
                      : statusValue === "processing"
                      ? "processing"
                      : "pending";

                  const displayName = tx?.client_name || tx?.user || "N/A";
                  const displayAmount = tx?.amount
                    ? `₦${Number(tx.amount).toLocaleString("en-NG", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`
                    : "N/A";

                  const displayDate =
                    tx?.date || tx?.created_at
                      ? new Date(
                          tx.date || tx.created_at || ""
                        ).toLocaleDateString("en-NG", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })
                      : "N/A";

                  const displayTime =
                    tx?.time || tx?.created_at
                      ? new Date(
                          tx.time || tx.created_at || ""
                        ).toLocaleTimeString("en-NG", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "";

                  return (
                    <tr
                      key={`${tx?.reference || tx?.id || index}-${index}`}
                      className="transition-colors duration-200 hover:bg-black/5"
                    >
                      <td>
                        <div className="font-medium">{displayName}</div>
                        {tx?.property_type && (
                          <div className="text-sm text-gray-500">
                            {tx.property_type}
                          </div>
                        )}
                      </td>
                      <td className="font-semibold">{displayAmount}</td>
                      <td>
                        <span
                          className={
                            statusTone === "success"
                              ? "badge badge--success"
                              : statusTone === "processing"
                              ? "badge badge--processing"
                              : "badge badge--pending"
                          }
                        >
                          {tx?.status?.toUpperCase() || "PENDING"}
                        </span>
                      </td>
                      <td>
                        <div>{displayDate}</div>
                        {displayTime && (
                          <div className="dashboard-table__time">
                            {displayTime}
                          </div>
                        )}
                      </td>
                      <td className="font-mono text-sm">
                        {tx?.reference || tx?.id || "N/A"}
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleViewTransaction(tx)}
                            className="dashboard-table__action"
                            type="button"
                            aria-label="View transaction"
                            disabled={isLoading || isDeleting === tx?.id}
                          >
                            <Eye size={14} />
                          </button>
                          <button
                            onClick={() => handleEditTransaction(tx)}
                            className="dashboard-table__action"
                            type="button"
                            aria-label="Edit transaction"
                            disabled={isLoading || isDeleting === tx?.id}
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            onClick={() =>
                              tx?.id && handleDeleteTransaction(tx.id)
                            }
                            className="dashboard-table__action dashboard-table__action--danger"
                            type="button"
                            aria-label="Delete transaction"
                            disabled={isLoading || isDeleting === tx?.id}
                          >
                            {isDeleting === tx?.id ? (
                              <ButtonLoader size="sm" />
                            ) : (
                              <Trash2 size={14} />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="dashboard-pagination">
          <div className="dashboard-pagination__info">
            <span>Page</span>
            <span className="dashboard-pagination__current">{currentPage}</span>
            <span>of {totalPages || 1}</span>
          </div>
          <div className="dashboard-pagination__controls">
            <button
              className="dashboard-pagination__button"
              type="button"
              disabled={currentPage === 1 || isLoading}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              aria-label="Previous page"
            >
              <ChevronDown size={16} className="rotate-90" />
            </button>
            <button
              className="dashboard-pagination__button"
              type="button"
              disabled={currentPage >= totalPages || isLoading}
              onClick={() => setCurrentPage((p) => p + 1)}
              aria-label="Next page"
            >
              <ChevronDown size={16} className="-rotate-90" />
            </button>
          </div>
        </div>
      </section>

      {/* Update Transaction Modal */}
      <Modal
        opened={isUpdateModalOpen}
        onClose={() => {
          setIsUpdateModalOpen(false);
          setSelectedTransaction(null);
          updateForm.reset();
        }}
        title="Update Transaction"
        size="lg"
      >
        <form
          onSubmit={updateForm.onSubmit(handleUpdateTransaction)}
          className="dashboard-form space-y-4"
        >
          <TextInput
            label="User Email"
            placeholder="user@example.com"
            type="email"
            disabled={isSubmitting}
            error={updateForm.errors.user}
            {...updateForm.getInputProps("user")}
          />

          <NumberInput
            label="Amount"
            placeholder="₦0.00"
            disabled={isSubmitting}
            error={updateForm.errors.amount}
            prefix="₦"
            thousandSeparator=","
            min={0}
            {...updateForm.getInputProps("amount")}
          />

          <Select
            label="Status"
            placeholder="Select status"
            data={statusOptions}
            disabled={isSubmitting}
            error={updateForm.errors.status}
            {...updateForm.getInputProps("status")}
          />

          <div className="dashboard-form__actions flex items-center justify-end gap-3 mt-6">
            <button
              className="dashboard-button dashboard-button--secondary"
              type="button"
              onClick={() => {
                setIsUpdateModalOpen(false);
                setSelectedTransaction(null);
                updateForm.reset();
              }}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              className="dashboard-button flex items-center justify-center gap-2"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <ButtonLoader color="white" />
                  <span>Updating...</span>
                </>
              ) : (
                "Update Transaction"
              )}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
